// Factory — industrial complex map.
// Rebuild focus: metal skeleton, red container clusters, pipes overhead,
// warm sodium lighting, oil drums, forklifts, catwalks and shelving.
import EnvironmentAtmosphere from "@/game/systems/Environment";
import {
    ConcreteWall,
    Crate,
    ShippingContainer,
    Barrel,
    Fence,
    Pipe,
    Sign,
    Ground,
    StreetLamp,
} from "@/game/systems/props";
import { MAT } from "@/game/systems/materials";

// Wall AABBs. Kept close to legacy layout so bots + collision still work.
export const FACTORY_WALLS = [
    // Perimeter (25x25)
    { min: [-25, 0, -25], max: [-24, 4, 25] },
    { min: [24, 0, -25], max: [25, 4, 25] },
    { min: [-25, 0, -25], max: [25, 4, -24] },
    { min: [-25, 0, 24], max: [25, 4, 25] },
    // Central conveyor
    { min: [-1, 0, -14], max: [1, 1.2, 14] },
    { min: [-3, 0, -12], max: [-1.5, 2.2, -10] },
    { min: [1.5, 0, -12], max: [3, 2.2, -10] },
    { min: [-3, 0, 10], max: [-1.5, 2.2, 12] },
    { min: [1.5, 0, 10], max: [3, 2.2, 12] },
    // Left shelving
    { min: [-18, 0, -6], max: [-15, 3.2, -3] },
    { min: [-18, 0, 3], max: [-15, 3.2, 6] },
    { min: [-14, 0, -14], max: [-10, 2.6, -12] },
    { min: [-14, 0, 12], max: [-10, 2.6, 14] },
    // Right shelving
    { min: [15, 0, -6], max: [18, 3.2, -3] },
    { min: [15, 0, 3], max: [18, 3.2, 6] },
    { min: [10, 0, -14], max: [14, 2.6, -12] },
    { min: [10, 0, 12], max: [14, 2.6, 14] },
    // Low cover
    { min: [-7, 0, 0], max: [-5, 1.4, 2.5] },
    { min: [5, 0, -2.5], max: [7, 1.4, 0] },
    // Pallets
    { min: [-9, 0, -4], max: [-7, 1, -2] },
    { min: [7, 0, 2], max: [9, 1, 4] },
    { min: [-11, 0, 6], max: [-9, 1, 8] },
    { min: [9, 0, -8], max: [11, 1, -6] },
    // L-shape corners
    { min: [-22, 0, -18], max: [-20, 3, -6] },
    { min: [20, 0, -18], max: [22, 3, -6] },
    { min: [-22, 0, 6], max: [-20, 3, 18] },
    { min: [20, 0, 6], max: [22, 3, 18] },
];

export const FACTORY_SPAWNS = {
    player: { position: [0, 1.7, 20], yaw: Math.PI },
    enemies: [[0, 1.7, -20], [-8, 1.7, -18], [8, 1.7, -18], [-16, 1.7, -8], [16, 1.7, -8]],
    allies: [[-4, 1.7, 20], [4, 1.7, 20], [-8, 1.7, 20], [8, 1.7, 20]],
};

export const FACTORY_WAYPOINTS = [
    [0, 0, -14], [-8, 0, -12], [8, 0, -12], [-17, 0, -8], [17, 0, -8],
    [-6, 0, -2], [6, 0, 2], [0, 0, 0], [-13, 0, 8], [13, 0, 8],
    [-6, 0, 14], [6, 0, 14],
];

export const FACTORY_META = {
    id: "factory",
    name: "Factory",
    walls: FACTORY_WALLS,
    spawns: FACTORY_SPAWNS,
    waypoints: FACTORY_WAYPOINTS,
    bounds: { minX: -24, maxX: 24, minZ: -24, maxZ: 24 },
    preview: {
        environment: "INDUSTRIAL COMPLEX",
        recommendedPlayers: "4 – 6",
        difficulty: "INTERMEDIATE",
        palette: ["#6b4a34", "#3a3c40", "#c58f2a"],
        tagline: "Overhead pipes, red containers and one long conveyor spine.",
    },
};

export default function Factory({ shadows, timeOfDay = "day", weather = "sunny" }) {
    return (
        <group>
            <EnvironmentAtmosphere timeOfDay={timeOfDay} weather={weather} shadows={shadows} />

            {/* Concrete factory floor */}
            <Ground material="concrete" size={80} />
            {/* Painted safety zone stripes on the floor */}
            {[-15, 0, 15].map((x) => (
                <mesh key={x} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.005, 0]} material={MAT.metalYellow()}>
                    <planeGeometry args={[0.15, 46]} />
                </mesh>
            ))}

            {/* Perimeter walls — brick/concrete blend */}
            <ConcreteWall position={[-24.5, 2, 0]} size={[1, 4, 50]} color="brick" shadows={shadows} />
            <ConcreteWall position={[24.5, 2, 0]} size={[1, 4, 50]} color="brick" shadows={shadows} />
            <ConcreteWall position={[0, 2, -24.5]} size={[50, 4, 1]} color="brick" shadows={shadows} />
            <ConcreteWall position={[0, 2, 24.5]} size={[50, 4, 1]} color="brick" shadows={shadows} />

            {/* Central conveyor spine */}
            <mesh position={[0, 0.6, 0]} receiveShadow material={MAT.metalDark()}>
                <boxGeometry args={[2, 1.2, 28]} />
            </mesh>
            {/* Conveyor rollers */}
            {Array.from({ length: 12 }).map((_, i) => (
                <mesh key={i} position={[0, 1.25, -13 + i * 2.2]} rotation={[0, 0, Math.PI / 2]} material={MAT.metalRust()}>
                    <cylinderGeometry args={[0.08, 0.08, 1.8, 8]} />
                </mesh>
            ))}
            {/* Boxes on the conveyor */}
            <Crate position={[0, 1.7, -11]} size={[1.2, 0.9, 1.2]} shadows={shadows} />
            <Crate position={[0, 1.7, -3]} size={[1.2, 0.9, 1.2]} shadows={shadows} />
            <Crate position={[0, 1.7, 5]} size={[1.2, 0.9, 1.2]} shadows={shadows} />

            {/* Shipping containers stacked on both sides */}
            <ShippingContainer position={[-16.5, 1.3, -4.5]} rotation={[0, 0, 0]} color="metalRed" size={[3, 2.6, 3]} shadows={shadows} />
            <ShippingContainer position={[-16.5, 1.3, 4.5]} color="metalYellow" size={[3, 2.6, 3]} shadows={shadows} />
            <ShippingContainer position={[16.5, 1.3, -4.5]} color="metalYellow" size={[3, 2.6, 3]} shadows={shadows} />
            <ShippingContainer position={[16.5, 1.3, 4.5]} color="metalRed" size={[3, 2.6, 3]} shadows={shadows} />

            {/* End-of-line container clusters */}
            <ShippingContainer position={[-12, 1.3, -13]} color="metalDark" size={[4, 2.6, 2]} shadows={shadows} />
            <ShippingContainer position={[12, 1.3, -13]} color="metalDark" size={[4, 2.6, 2]} shadows={shadows} />
            <ShippingContainer position={[-12, 1.3, 13]} color="metalRust" size={[4, 2.6, 2]} shadows={shadows} />
            <ShippingContainer position={[12, 1.3, 13]} color="metalRust" size={[4, 2.6, 2]} shadows={shadows} />

            {/* Low pallet cover */}
            {[[-8, -3], [8, 3], [-10, 7], [10, -7]].map(([x, z], i) => (
                <group key={i} position={[x, 0.5, z]}>
                    <mesh material={MAT.woodLight()}>
                        <boxGeometry args={[2, 1, 2]} />
                    </mesh>
                    <mesh position={[0, 0.55, 0]} material={MAT.wood()}>
                        <boxGeometry args={[2.05, 0.06, 2.05]} />
                    </mesh>
                </group>
            ))}

            {/* Forklift-shaped cover */}
            <group position={[-6, 0.7, 1.25]}>
                <mesh material={MAT.metalYellow()}>
                    <boxGeometry args={[2, 1.4, 2.5]} />
                </mesh>
                {/* Mast */}
                <mesh position={[0, 1.6, 1.2]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.15, 2, 0.15]} />
                </mesh>
            </group>
            <group position={[6, 0.7, -1.25]}>
                <mesh material={MAT.metalYellow()}>
                    <boxGeometry args={[2, 1.4, 2.5]} />
                </mesh>
                <mesh position={[0, 1.6, -1.2]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.15, 2, 0.15]} />
                </mesh>
            </group>

            {/* Overhead pipes — visual only, no collision */}
            {[-8, -4, 4, 8].map((z) => (
                <Pipe key={`pipe-${z}`} position={[0, 5, z]} rotation={[0, 0, Math.PI / 2]} length={45} radius={0.18} />
            ))}
            {/* Vertical drop-down pipe */}
            <Pipe position={[-20, 3, 0]} length={6} radius={0.15} />
            <Pipe position={[20, 3, 0]} length={6} radius={0.15} />

            {/* Barrels dotted around */}
            <Barrel position={[-19, 0.55, 12]} color="metalRust" shadows={shadows} />
            <Barrel position={[-18.5, 0.55, 11.6]} color="metalRed" shadows={shadows} />
            <Barrel position={[19, 0.55, -12]} color="metalYellow" shadows={shadows} />
            <Barrel position={[18.5, 0.55, -11.6]} color="metalRust" shadows={shadows} />
            <Barrel position={[8, 0.55, -14]} color="metalRust" shadows={shadows} />
            <Barrel position={[-8, 0.55, 14]} color="metalRed" shadows={shadows} />

            {/* Warning fences on the sides */}
            <Fence position={[-21, 0, -12]} rotation={[0, Math.PI / 2, 0]} length={4} shadows={shadows} />
            <Fence position={[21, 0, -12]} rotation={[0, Math.PI / 2, 0]} length={4} shadows={shadows} />

            {/* Signs — hazard triangles */}
            <Sign position={[-3, 0, -22]} color="signBlue" />
            <Sign position={[3, 0, -22]} rotation={[0, -0.2, 0]} color="signBlue" />

            {/* Overhead sodium lamps */}
            {[-16, 0, 16].map((x) =>
                [-14, 0, 14].map((z) => (
                    <group key={`lamp-${x}-${z}`} position={[x, 5.6, z]}>
                        <mesh material={MAT.metalDark()}>
                            <boxGeometry args={[0.8, 0.15, 0.5]} />
                        </mesh>
                        <mesh position={[0, -0.1, 0]} material={MAT.lightWarm()}>
                            <boxGeometry args={[0.7, 0.05, 0.4]} />
                        </mesh>
                        <pointLight
                            position={[0, -0.5, 0]}
                            intensity={1.0}
                            color="#ffb26b"
                            distance={12}
                            decay={2}
                        />
                    </group>
                ))
            )}

            {/* Outside grass ring (visible above wall) */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} material={MAT.grassDry()}>
                <planeGeometry args={[140, 140]} />
            </mesh>
        </group>
    );
}
